import { useState, useCallback } from 'react';
import axios from 'axios';
import compressImage from './image_compressor';
import appAxios from '../components/axios';

const PRESIGN_URL = '/api/s3/cache/presign';

/**
 * Presigned URLでクラウドに直接画像をアップロードするReactフック
 *
 * @returns {{ upload: (file: File) => Promise<string>, uploading: boolean }}
 *   upload - ファイルを受け取り、Shrine cache JSONを返す
 *   uploading - アップロード中かどうか
 */
const usePresignedImageUpload = () => {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (rawFile, options = {}) => {
    setUploading(true);

    try {
      const file = await compressImage(rawFile);

      // Step 1: Railsからpresigned URLを取得
      const presignRes = await appAxios.get(PRESIGN_URL, {
        params: { filename: file.name, type: file.type },
      });
      const { url, fields, headers } = presignRes.data;

      // Step 2: クラウドに直接アップロード
      if (fields && Object.keys(fields).length > 0) {
        // POST multipart (S3 post policy)
        const formData = new FormData();
        Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);
        await axios.post(url, formData);
      } else {
        // PUT style
        await axios.put(url, file, {
          headers: { 'Content-Type': file.type, ...headers },
        });
      }

      // Step 3: Shrine cache JSONを構築
      // presignのkeyにはストレージprefixが含まれるため、相対IDに変換
      const storageName = options.storageName || 'cache';
      const id = (fields?.key || new URL(url).pathname).split('/').pop();
      const cacheData = JSON.stringify({
        id,
        storage: storageName,
        metadata: {
          filename: file.name,
          size: file.size,
          mime_type: file.type,
        },
      });

      return cacheData;
    } finally {
      setUploading(false);
    }
  }, []);

  return { upload, uploading };
};

export default usePresignedImageUpload;
